import type { DeliveryChannel, DeliverySkipReason, InterviewBookingMessageStoreResponse } from '@jobilla/nexus-sdk'

export type ChannelCount = { channel: DeliveryChannel; count: number }
export type ChannelSkipCount = { channel: DeliveryChannel; reason: DeliverySkipReason | null; count: number }

export type DeliverySummary = {
	/** Every requested channel on every token was delivered — the clean success case. */
	allDelivered: boolean
	/** Channels that reached the candidate, per channel — the good news, always shown. */
	sentCounts: ChannelCount[]
	/**
	 * Retryable transport failures, one entry per (token, channel). Kept granular
	 * because the retry request is built from these; the UI shows only the counts.
	 */
	failures: Array<{ tokenId: string; channel: DeliveryChannel }>
	/** Retryable failures aggregated by channel — what the summary renders. */
	failureCounts: ChannelCount[]
	/**
	 * Deliberately-not-attempted channels aggregated by (channel, reason). Not
	 * retryable as-is — the reason must be fixed first. A skip with no reason is
	 * still counted (reason === null) so it can never masquerade as delivered.
	 */
	skipCounts: ChannelSkipCount[]
}

/**
 * Collapses the per-token, per-channel API outcomes into aggregate counts for
 * every outcome — sent, failed, and skipped. Surfacing the sent count matters as
 * much as the failures: a bulk send where 30 of 100 landed must not read as a
 * total failure just because the 70 problems are louder. Failures stay granular
 * internally only to rebuild the retry request.
 */
export function summarizeDeliveryOutcomes(outcomes: InterviewBookingMessageStoreResponse): DeliverySummary {
	const failures: DeliverySummary['failures'] = []
	const sentCounts = new Map<DeliveryChannel, number>()
	const failureCounts = new Map<DeliveryChannel, number>()
	const skipCounts = new Map<string, ChannelSkipCount>()

	for (const token of outcomes) {
		for (const channel of token.channels) {
			if (channel.outcome === 'sent') {
				sentCounts.set(channel.channel, (sentCounts.get(channel.channel) ?? 0) + 1)
			} else if (channel.outcome === 'failed') {
				failures.push({ tokenId: token.tokenId, channel: channel.channel })
				failureCounts.set(channel.channel, (failureCounts.get(channel.channel) ?? 0) + 1)
			} else if (channel.outcome === 'skipped') {
				// Count every skip, even one with no reason — otherwise a skipped-but-
				// undelivered channel silently drops out and allDelivered goes wrongly true.
				const reason = channel.reason ?? null
				const key = `${channel.channel}:${reason ?? 'unknown'}`
				const existing = skipCounts.get(key)

				if (existing) {
					existing.count += 1
				} else {
					skipCounts.set(key, { channel: channel.channel, reason, count: 1 })
				}
			}
		}
	}

	return {
		allDelivered: failures.length === 0 && skipCounts.size === 0,
		sentCounts: toChannelCounts(sentCounts),
		failures,
		failureCounts: toChannelCounts(failureCounts),
		skipCounts: [...skipCounts.values()],
	}
}

function toChannelCounts(counts: Map<DeliveryChannel, number>): ChannelCount[] {
	return [...counts.entries()].map(([channel, count]) => ({ channel, count }))
}

/** Adds two per-channel count lists together — used to keep the running sent total after a retry. */
export function mergeChannelCounts(first: ChannelCount[], second: ChannelCount[]): ChannelCount[] {
	const merged = new Map<DeliveryChannel, number>()

	for (const entry of [...first, ...second]) {
		merged.set(entry.channel, (merged.get(entry.channel) ?? 0) + entry.count)
	}

	return toChannelCounts(merged)
}

/** The distinct channels that failed, for building a retry request. */
export function failedChannels(summary: DeliverySummary): DeliveryChannel[] {
	return [...new Set(summary.failures.map((failure) => failure.channel))]
}

/** The distinct token ids that had at least one failed channel. */
export function failedTokenIds(summary: DeliverySummary): string[] {
	return [...new Set(summary.failures.map((failure) => failure.tokenId))]
}
